import List from '@/components/common/List';
import { useApplication } from '@/hooks/useApplication';
import { useMetadata } from '@/hooks/useMetadata';
import { Detail } from '@/types/detail';
import { ConfigurationType } from '@/types/metadata/metadata';
import { Severity } from '@/types/validation';
import { VariableRef } from '@/types/variable';
import { findSubroutine } from '@/util/application';
import { boundReference, padToArity } from '@/util/arity';
import { isEmptyValue, isString } from '@/util/typeGuards';
import { returnvariablesUtil } from '@/util/validation/returnvariables';
import { variableUtil } from '@/util/validation/variable';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Card, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { FC } from 'react';
import { ParamProps } from './ParamRenderer';
import VariableView from './VariableView';

const ParamReturnVariables: FC<ParamProps> = (props) => {
  const theme = useTheme();
  const { application } = useApplication();
  const { metadata } = useMetadata();

  const values = returnvariablesUtil().getValue(props.configuration, props.initialValue) || [];

  const chosenSubStart = boundReference(props.configuration, props.initialValue as Detail);
  const chosenSubroutine = findSubroutine(application.subroutines, chosenSubStart);

  if (!props.parentId || !chosenSubroutine) return <></>;

  const node = application.blocks.nodes[props.nodeId];
  const blockName = `${props.nodeId}: ${node.type}${node.label && ` (${node.label})`}`;

  const getErrors = (index: number) => {
    let result = undefined;
    if (values?.length) {
      const returnType = chosenSubroutine.return_types[index];
      const type = isString(returnType) ? returnType : returnType?.type;
      const varErrors = variableUtil().validate(
        application,
        metadata,
        props.parentId,
        props.nodeId,
        blockName,
        props.configuration,
        { ...values[index], type }
      );
      if (varErrors.length) {
        result = varErrors[0].severity;
      } else {
        if (values[index]?.scope === 'local') {
          const subVars = application.variables.local?.[props.parentId];
          const subVar = subVars?.find((subvar) => subvar.name === values[index].name);
          if (type && subVar?.type !== type) {
            result = 'error' as Severity;
          }
        }
      }
    }

    return result;
  };

  return (
    <Card variant='outlined'>
      <Stack
        gap={1}
        sx={{ minHeight: '4rem' }}
        p={1}
        {...(props.errors?.length && { color: theme.palette.error.main })}
      >
        <List subheader={props.configuration.label + (props.configuration.required ? ' *' : '')} />
        {!chosenSubroutine.return_types?.length ? (
          <Typography variant='body1' fontStyle='italic' color='text.disabled' p={2} mt={-2}>
            No {props.configuration.label.toLocaleLowerCase()} available
          </Typography>
        ) : (
          chosenSubroutine.return_types.map((returnType, index) => {
            const type = isString(returnType) ? returnType : returnType.type;
            return (
              <VariableView
                key={`returnvariable.${index}`}
                subroutineId={props.parentId}
                label={returnType.label || `${props.configuration.label} ${index + 1}`}
                type={type as ConfigurationType}
                initialValue={values[index] as VariableRef}
                allowEmpty
                onChange={(newVariable) => {
                  const newInitialValue = [...values];
                  // Clearing a row is the user marking it empty, which a slot nobody ever filled is not.
                  newInitialValue[index] = (newVariable ?? { empty: true }) as Detail;
                  props.onChange([
                    props.configuration.value,
                    padToArity(newInitialValue, chosenSubroutine.return_types.length)
                  ]);
                }}
                errorSeverity={getErrors(index)}
              />
            );
          })
        )}
        {values.map((value, index) => {
          if (!chosenSubroutine.return_types || !chosenSubroutine.return_types[index]) {
            return (
              <Box
                key={`node.${props.nodeId}.args.${index}`}
                sx={{ display: 'flex', color: 'red', alignItems: 'center', gap: 1, pl: 2 }}
              >
                {props.configuration.required && <Typography variant='body1'>*</Typography>}
                <Typography variant='caption'>
                  Return type {index + 1} - {(value?.name ?? isEmptyValue(value)) ? 'empty' : value?.toString()}
                </Typography>
                <IconButton onClick={() => props.onChange([props.configuration.value, values.toSpliced(index, 1)])}>
                  <CloseIcon />
                </IconButton>
              </Box>
            );
          }
        })}
      </Stack>
    </Card>
  );
};

export default ParamReturnVariables;
